import {
  Breadcrumb,
  BreadcrumbIcon,
  BreadcrumbLink,
  BreadcrumbPage,
  Button,
  useAlert,
} from '@ads/components-react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  COUNTRY_PAGE_SIZE,
  CountryFormModal,
  CountrySearchBar,
  CountryTable,
  DeleteCountryModal,
  useCountryFilters,
  useCountryList,
  useDeleteCountry,
} from '@/features/location';
import type { CountryViewModel } from '@/shared/api/generated';
import { PageHeader } from '@/shared/ui/organisms/PageHeader';

export const Route = createFileRoute('/_authenticated/location/countries/')({
  component: CountryListPage,
});

/** Página de listagem de Países (área de Localização). */
export function CountryListPage() {
  const { t } = useTranslation('location');
  const { alert } = useAlert();
  const navigate = useNavigate();

  const { countries, isLoading, isError, refetch } = useCountryList();
  const { filters, updateFilters } = useCountryFilters();
  const deleteCountry = useDeleteCountry();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<CountryViewModel | null>(null);
  const [deleting, setDeleting] = useState<CountryViewModel | null>(null);

  // TODO: busca client-side — o endpoint `GET /api/v1/location/countries` ainda
  // não aceita parâmetro de busca. Quando o backend implementar (ex.: `?search=`),
  // enviar `filters.search` via `useCountryList` e remover a filtragem abaixo.
  const searched = useMemo(() => {
    const query = filters.search.trim().toLowerCase();
    if (!query) return countries;
    return countries.filter(
      country =>
        (country.name ?? '').toLowerCase().includes(query) ||
        (country.code ?? '').toLowerCase().includes(query),
    );
  }, [countries, filters.search]);

  // TODO: paginação client-side — o endpoint retorna a lista completa. Quando o
  // backend paginar (page/pageSize/total), passar esses parâmetros para
  // `useCountryList`, usar os totais do envelope e remover o fatiamento. Ver `COUNTRY_PAGE_SIZE`.
  const totalPages = Math.max(1, Math.ceil(searched.length / COUNTRY_PAGE_SIZE));
  const currentPage = Math.min(filters.page, totalPages);
  const pageData = useMemo(() => {
    const start = (currentPage - 1) * COUNTRY_PAGE_SIZE;
    return searched.slice(start, start + COUNTRY_PAGE_SIZE);
  }, [searched, currentPage]);

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (country: CountryViewModel) => {
    setEditing(country);
    setFormOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (deleting?.id == null) return;
    deleteCountry.mutate(
      { id: deleting.id },
      {
        onSuccess: () => alert({ alertType: 'success', title: t('countries.toast.deleteSuccess') }),
        onError: () => alert({ alertType: 'warning', title: t('countries.toast.deleteError') }),
        onSettled: () => setDeleting(null),
      },
    );
  };

  const addButton = (
    <Button hierarchy="primary" size="lg" leadingIcon={PlusIcon} onClick={openCreate}>
      {t('countries.actions.add')}
    </Button>
  );

  return (
    <div className="flex flex-col gap-xxs">
      <PageHeader
        title={t('countries.title')}
        subtitle={t('countries.subtitle', { count: countries.length })}
        breadcrumb={
          <Breadcrumb>
            <BreadcrumbLink>
              <Link to="/">
                <BreadcrumbIcon />
              </Link>
            </BreadcrumbLink>
            <BreadcrumbPage>{t('countries.breadcrumb.countries')}</BreadcrumbPage>
          </Breadcrumb>
        }
        actions={addButton}
      />

      {isError ? (
        <div className="flex flex-col items-start gap-nano rounded-md border-hairline border-base-danger-300 bg-ctx-layout-surface p-xxs">
          <p className="text-base-danger-700 ts-paragraph-xs">{t('countries.toast.loadError')}</p>
          <Button hierarchy="secondary" size="md" onClick={() => void refetch()}>
            {t('countries.loadErrorRetry')}
          </Button>
        </div>
      ) : (
        <>
          <CountrySearchBar
            defaultValue={filters.search}
            onSearch={search => updateFilters({ search, page: 1 })}
            onClear={() => updateFilters({ search: '', page: 1 })}
          />

          <CountryTable
            data={pageData}
            isLoading={isLoading}
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={searched.length}
            pageSize={COUNTRY_PAGE_SIZE}
            onPageChange={page => updateFilters({ page })}
            emptyStateAction={addButton}
            onManageLangs={country =>
              void navigate({
                to: '/location/countries/$countryId/langs',
                params: { countryId: String(country.id) },
              })
            }
            onEdit={openEdit}
            onDelete={setDeleting}
          />
        </>
      )}

      <CountryFormModal
        open={formOpen}
        country={editing}
        onOpenChange={open => {
          setFormOpen(open);
          if (!open) setEditing(null);
        }}
      />

      <DeleteCountryModal
        open={deleting !== null}
        countryName={deleting?.name ?? ''}
        onConfirm={handleDeleteConfirm}
        onOpenChange={open => {
          if (!open) setDeleting(null);
        }}
      />
    </div>
  );
}
